import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  Switch,
  Typography,
} from '@mui/material';
import { ReactElement, useEffect, useRef, useState, useMemo } from 'react';
import { ReactComponent as ChevronDown } from 'assets/images/ic_chevron_down.svg';
import { ReactComponent as Close } from 'assets/images/ic_close.svg';
import {
  AppFormikTextField,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { Formik, FormikProps } from 'formik';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { useSelector } from 'react-redux';
import { AuthState } from 'state/authentication/Reducer';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { SettingService } from 'services/SettingService';
import { Utils } from 'shared/helpers';
import * as Yup from 'yup';

interface AIUseCategory {
  _id: string;
  category_name: string;
  description: string;
  isActive: boolean;
  organizationId: string;
  sort_order: number;
  organization_ai_use_settings?: Array<{ is_enabled: boolean }>;
  enabled?: boolean;
}

interface CategoryFormValues {
  is_enabled: boolean[];
}

export interface Policies {
  total: number;
  published: number;
}

export interface AiSystems {
  total: number;
  approved: number;
}

export interface Datasets {
  total: number;
  approved: number;
}

export interface IModuleSummary {
  policies: Policies;
  aiSystems: AiSystems;
  datasets: Datasets;
  framework: number;
}
export interface IReminders {
  _id: string;
  acknowledgementReminderInDays: any[];
  assessmentReminderIntervalInDays: any[];
  type: string;
  isActive: boolean;
  userId: any;
  deletedAt: any;
  organizationId: any;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const InformationCard = (): ReactElement => {
  const [isCollapsableFeature, setCollapsableFeature] =
    useState<boolean>(false);
  const {
    authState,
    baseAppState,
  }: { authState: AuthState; baseAppState: BaseApplicationState } = useSelector(
    (state: any) => state
  );
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const { name, role } = authState;

  const [aiUseCategories, setAiUseCategories] = useState<AIUseCategory[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [updatingCategoryId, setUpdatingCategoryId] = useState<string | null>(
    null
  );
  const [reminderlist, setReminderList] = useState<IReminders>(
    {} as IReminders
  );
  const [initialReminderList, setInitialReminderList] = useState<IReminders>(
    {} as IReminders
  );
  const [isReminder, setIsReminder] = useState(false);
  const [isSaveReminder, setIsSaveReminder] = useState(false);

  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [moduleSummary, setModuleSummary] = useState<IModuleSummary>({
    policies: {
      total: 0,
      published: 0,
    },
    aiSystems: {
      total: 0,
      approved: 0,
    },
    datasets: {
      total: 0,
      approved: 0,
    },
    framework: 0,
  });
  const formikRefs = useRef<Map<string, FormikProps<CategoryFormValues>>>(
    new Map()
  );

  const getInitialFormValues = (enabled: boolean): CategoryFormValues => ({
    is_enabled: enabled ? [false, true] : [true, false],
  });

  const loadAIUseCategories = (): void => {
    setIsLoadingDetail(true);
    SettingService.getAll()
      .then((response) => {
        const { data } = response.data;
        const categoriesWithState = data.map((category: AIUseCategory) => ({
          ...category,
          enabled:
            category.organization_ai_use_settings?.[0]?.is_enabled ?? true,
        }));
        setAiUseCategories(categoriesWithState);
      })
      .catch((error) => {
        console.error('Failed to load AI use categories:', error);
      })
      .finally(() => {
        setIsLoadingDetail(false);
      });
  };

  const loadSummaryCount = (): void => {
    setIsLoadingSummary(true);
    SettingService.getModulesSummary()
      .then((response) => {
        console.log(response.data);
        const { data } = response.data;
        setModuleSummary(data);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoadingSummary(false);
      });
  };

  const loadReminders = (): void => {
    setIsReminder(true);
    SettingService.getRemiderList()
      .then((res) => {
        const { data } = res.data;
        setReminderList(data);
        setInitialReminderList(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsReminder(false);
      });
  };

  const actionOnAddReminder = (data: any, formik: any): void => {
    const reminderDay = Number(data.reminderDay);
    setReminderList((state: any) => ({
      ...state,
      acknowledgementReminderInDays: [
        ...(state.acknowledgementReminderInDays || []),
        reminderDay,
      ],
    }));

    formik.resetForm();
    formik.setSubmitting(false);
  };

  const actionOnDeleteReminder = (reminder: number): void => {
    setReminderList((state: any) => ({
      ...state,
      acknowledgementReminderInDays:
        state.acknowledgementReminderInDays.filter(
          (item: number) => item !== reminder
        ) || [],
    }));
  };

  const acionOnSaveReminders = (): void => {
    const requestObj = {
      acknowledgementReminderInDays:
        reminderlist.acknowledgementReminderInDays || [],
      assessmentReminderIntervalInDays:
        reminderlist.assessmentReminderIntervalInDays || [],
    };
    setIsSaveReminder(true);
    SettingService.addRemider(requestObj)
      .then((res) => {
        Utils.handleSuccessResponse(res);
        loadReminders();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsSaveReminder(false);
      });
  };

  const handleToggleCategory = (
    categoryId: string,
    isEnabled: boolean
  ): void => {
    // Don't call API if already updating
    if (updatingCategoryId === categoryId) return;

    console.log('handleToggleCategory called:', { categoryId, isEnabled });
    setUpdatingCategoryId(categoryId);

    const category = aiUseCategories.find((c) => c._id === categoryId);
    if (!category) return;

    // Don't call API if the state is already the same
    if (category.enabled === isEnabled) {
      console.log('State already matches, skipping API call');
      setUpdatingCategoryId(null);
      return;
    }

    SettingService.updateCategoryStatus(categoryId, isEnabled)
      .then((response) => {
        console.log('API Response:', response);

        // Update local state immediately
        setAiUseCategories((prevCategories) =>
          prevCategories.map((c) =>
            c._id === categoryId ? { ...c, enabled: isEnabled } : c
          )
        );
      })
      .catch((error) => {
        console.error('Failed to update category status:', error);

        // Revert the formik state on error
        const formikRef = formikRefs.current.get(categoryId);
        if (formikRef) {
          const previousValues = getInitialFormValues(!isEnabled);
          formikRef.setValues(previousValues);
        }
      })
      .finally(() => {
        setUpdatingCategoryId(null);
      });
  };

  const isReminderChanged = useMemo(
    () =>
      JSON.stringify(reminderlist?.acknowledgementReminderInDays || []) !==
        JSON.stringify(
          initialReminderList?.acknowledgementReminderInDays || []
        ) ||
      JSON.stringify(reminderlist?.assessmentReminderIntervalInDays || []) !==
        JSON.stringify(
          initialReminderList?.assessmentReminderIntervalInDays || []
        ),
    [reminderlist, initialReminderList]
  );

  useEffect(() => {
    loadAIUseCategories();
    loadSummaryCount();
    loadReminders();
  }, []);

  return (
    <Box>
      <Paper variant="outlined">
        <Typography variant="h4">Account Information</Typography>
        <Stack className="account-information" direction="column">
          <Box className="label-information">
            <Typography variant="h6">Name</Typography>
            <Typography component="span">{name}</Typography>
          </Box>
          <Box className="label-information">
            <Typography variant="h6">Organisation</Typography>
            <Typography component="span">
              {baseAppState.activeOrg.name}
            </Typography>
          </Box>
          <Box className="label-information">
            <Typography variant="h6">Role</Typography>
            <Typography component="span">{role || ''}</Typography>
          </Box>
        </Stack>
      </Paper>
      <Paper variant="outlined">
        <Typography variant="h4">Modules Summary</Typography>
        <Stack direction="column" spacing={4}>
          {isLoadingSummary && (
            <Box className="empty-content">
              <CircularProgress />{' '}
            </Box>
          )}
          {!isLoadingSummary && (
            <Box className="card-tier-feature">
              <Card variant="outlined">
                <CardHeader
                  title="AI Policies & Attestation"
                  subheader={`${moduleSummary.policies.total} total, ${moduleSummary.policies.published} published`}
                />
              </Card>
              <Card variant="outlined">
                <CardHeader
                  title="AI Inventory & Approval"
                  subheader={`${moduleSummary.aiSystems.total} total, ${moduleSummary.aiSystems.approved} approved`}
                />
              </Card>
              <Card variant="outlined">
                <CardHeader
                  title="Dataset Inventory & Approval"
                  subheader={`${moduleSummary.datasets.total} total, ${moduleSummary.datasets.approved} approved`}
                />
              </Card>
              <Card variant="outlined">
                <CardHeader
                  title="AI Frameworks"
                  subheader={`${moduleSummary.framework} unique framework`}
                />
              </Card>
            </Box>
          )}

          {false && (
            <Box className="additional-features">
              <Card variant="outlined">
                <CardHeader
                  title="View Additional Features "
                  action={
                    <IconButton
                      onClick={() => {
                        setCollapsableFeature(!isCollapsableFeature);
                      }}
                    >
                      <SvgIcon
                        component={ChevronDown}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="#76A81F"
                      />
                    </IconButton>
                  }
                />
                <Collapse
                  in={isCollapsableFeature}
                  timeout="auto"
                  unmountOnExit
                >
                  <CardContent>
                    <Typography>sss</Typography>
                  </CardContent>
                </Collapse>
              </Card>
            </Box>
          )}
          {false && (
            <Box className="compliance-quest">
              <Typography variant="h6">
                Ready to Increase Your Compliance Capacity?
              </Typography>
              <Typography component="span">
                Upgrade and manage your orginization's SovereignAI plan.
              </Typography>
              <Box>
                <AppPrimaryButton title="Manage Plan" />
              </Box>
            </Box>
          )}
          {isReminder && Object.values(reminderlist).length === 0 && (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          )}
          {Object.values(reminderlist).length > 0 && (
            <Box className="acknowledge-card">
              <Card variant="outlined">
                <CardHeader title="Policy acknowledgement reminders" />
                <CardContent>
                  <Typography variant="body2">
                    Reminder schedule (days after assignment)
                  </Typography>
                  <Typography>
                    Configure when reminder emails are sent to employees who
                    haven't acknowledged policies. Reminders automatically stop
                    after 60 days.
                  </Typography>
                  {reminderlist.acknowledgementReminderInDays.map(
                    (reminder: any) => (
                      <Chip
                        label={`Day ${reminder}`}
                        variant="outlined"
                        onDelete={() => actionOnDeleteReminder(reminder)}
                        deleteIcon={
                          <SvgIcon
                            component={Close}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        }
                      />
                    )
                  )}
                  <Box className="acknowledge-day-input">
                    <Formik
                      initialValues={{ reminderDay: '' }}
                      validationSchema={Yup.object().shape({
                        reminderDay: Yup.number()
                          .min(1, '')
                          .max(60, '')
                          .required('')
                          .test('duplicate-reminder', '', (value) => {
                            if (!value) return true;

                            const existingReminderDays =
                              reminderlist.acknowledgementReminderInDays || [];

                            return !existingReminderDays.includes(
                              Number(value)
                            );
                          }),
                      })}
                      enableReinitialize
                      onSubmit={(formValues, formik) => {
                        actionOnAddReminder(formValues, formik);
                      }}
                      validateOnMount
                      validateOnBlur
                    >
                      {({ handleSubmit, isSubmitting, isValid }) => (
                        <form autoComplete="off">
                          <Stack direction="row" spacing={2}>
                            <AppFormikTextField
                              type="number"
                              size="medium"
                              outerLabel={false}
                              name="reminderDay"
                              placeholder="Add reminder day (1-60)"
                            />
                            {orgACLConfig.settings?.includes(
                              'settings.account.addPolicy'
                            ) && (
                              <AppPrimaryButton
                                className="mt-4 btn-small"
                                title="Add"
                                onClick={handleSubmit}
                                disabled={isSubmitting || !isValid}
                                startIcon={
                                  <SvgIcon
                                    component={Plus}
                                    inheritViewBox
                                    className="view-icon"
                                    fontSize="inherit"
                                    htmlColor="#76A81F"
                                  />
                                }
                              />
                            )}
                          </Stack>
                        </form>
                      )}
                    </Formik>
                  </Box>
                </CardContent>
                <CardActions>
                  {orgACLConfig.settings?.includes(
                    'settings.account.saveSettings'
                  ) && (
                    <AppPrimaryButton
                      title="Save Settings"
                      onClick={acionOnSaveReminders}
                      loadingText="Saving"
                      disabled={!isReminderChanged}
                      isLoading={isSaveReminder}
                    />
                  )}
                </CardActions>
              </Card>
            </Box>
          )}
          <Box className="category-box">
            <Typography variant="h6">AI Use Categories</Typography>
            <Typography component="span">
              Enable or disable AI use categories for your organisation. Enabled
              categories will be available when categorizing AI inventory items.
            </Typography>
            {isLoadingDetail ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress />
              </Box>
            ) : (
              <Box className="categories-card">
                <Grid container className="category-container">
                  {aiUseCategories?.map((category: AIUseCategory) => (
                    <Grid
                      item
                      className="category-setup"
                      xs={12}
                      key={category._id}
                    >
                      <Card variant="outlined">
                        <CardHeader
                          sx={{
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            position: 'relative',
                          }}
                          title={category.category_name}
                          subheader={category.description}
                          action={
                            <Box display="flex" alignItems="center" gap={1}>
                              {updatingCategoryId === category._id && (
                                <CircularProgress size={24} />
                              )}
                              <Formik
                                enableReinitialize
                                initialValues={getInitialFormValues(
                                  category.enabled ?? true
                                )}
                                onSubmit={() => {}}
                                innerRef={(ref) => {
                                  if (ref) {
                                    formikRefs.current.set(category._id, ref);
                                  }
                                }}
                              >
                                {() => (
                                  <Switch
                                    checked={category.enabled ?? true}
                                    onChange={(e) => {
                                      if (
                                        orgACLConfig.settings?.includes(
                                          'settings.account.aiusecategories'
                                        )
                                      ) {
                                        const newValue = e.target.checked;
                                        handleToggleCategory(
                                          category._id,
                                          newValue
                                        );
                                      }
                                    }}
                                    disabled={
                                      updatingCategoryId === category._id
                                    }
                                    color="success"
                                  />
                                )}
                              </Formik>
                            </Box>
                          }
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            <Typography component="span">
              Note: Disabled categories will not appear in the AI inventory
              categorization options. This helps you focus on the AI use cases
              relevant to your organisation.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default InformationCard;
